<template>
	<div class="row">
		<div class="col-12 col-xl-5">
			<div class="box invoice-supplier-details__document">
				<iframe v-if="is_pdf" :src="invoice.suppliersocr_pdf_url" class="invoice-supplier-fiche__preview"></iframe>
				<img v-else-if="invoice.suppliersocr_pdf_url" :src="invoice.suppliersocr_pdf_url" class="w-100" />
				<p v-else class="mb-0">{{ $t('invoice.supplier_fiche_no_preview') }}</p>
			</div>
		</div>

		<div class="col-12 col-xl-7">
			<div class="box invoice-supplier-details">
				<LoadingSpinner v-if="!loaded" />

				<!-- Les comptes proposés sont ceux de la comptabilité de l'entité destinataire : sans elle,
				     il n'y a rien à ventiler. -->
				<p v-else-if="!invoice.accounting_plan" class="mb-0">{{ $t('invoice.supplier_ventilation2_no_plan') }}</p>

				<template v-else>
					<!-- Cette vue n'affecte pas les chevaux : réenregistrer une ventilation qui en portait les
					     effacerait, et avec eux l'historique des charges par cheval. -->
					<b-alert v-if="has_horses" show variant="warning">{{ $t('invoice.supplier_ventilation2_horses_lost') }}</b-alert>

					<b-form-radio-group
						v-model="mode"
						:options="mode_options"
						buttons
						button-variant="outline-secondary"
						class="mb-3"
						@change="refresh"
					/>

					<!-- Pas de conteneur défilant autour des tableaux : il couperait la liste d'un compte ouverte. -->
					<table v-if="mode === 'rate'" class="table mb-0 invoice-supplier-details__table">
						<thead>
							<tr>
								<th class="text-right">{{ $t('invoice.supplier_ventilation2_ht') }}</th>
								<th>{{ $t('invoice.supplier_ventilation2_rate') }}</th>
								<th>{{ $t('invoice.supplier_ventilation2_charge_account') }}</th>
								<th class="text-right">{{ $t('invoice.supplier_ventilation2_vat') }}</th>
								<th>{{ $t('invoice.supplier_ventilation2_vat_account') }}</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(row, index) in rate_rows" :key="'rate-' + index">
								<td class="text-right"><PriceInput v-model="row.amount" @change="refresh" /></td>
								<td>
									<b-form-select v-model="row.rate" :options="rate_options" @change="onRateChanged(row)" />
								</td>
								<td class="invoice-supplier-ventilation__account">
									<e-select
										v-model="row.account"
										track-by="id"
										label="name"
										:options="charge_accounts"
										:searchable="true"
										:show-labels="false"
										:allow-empty="false"
										:placeholder="$t('compta.search_accounting_account')"
										@input="refresh"
									>
										<template slot="noOptions">{{ $t('global.list_empty') }}</template>
									</e-select>
								</td>
								<td class="text-right"><PriceInput v-model="row.vat_amount" @change="refresh" /></td>
								<td class="invoice-supplier-ventilation__account">
									<e-select
										v-model="row.vat_account"
										track-by="id"
										label="name"
										:options="vatAccountsFor(row.rate)"
										:disabled="!toCents(row.vat_amount)"
										:searchable="true"
										:show-labels="false"
										:allow-empty="false"
										:placeholder="$t('compta.search_accounting_account')"
										@input="refresh"
									>
										<template slot="noOptions">{{ $t('global.list_empty') }}</template>
									</e-select>
								</td>
								<td class="text-center">
									<a v-if="rate_rows.length > 1" href="#" @click.prevent="removeRateRow(index)">
										<font-awesome-icon :icon="['fal', 'trash-alt']" />
									</a>
								</td>
							</tr>
						</tbody>
					</table>

					<table v-else class="table mb-0 invoice-supplier-details__table">
						<thead>
							<tr>
								<th>{{ $t('invoice.supplier_ventilation2_article') }}</th>
								<th class="text-right">{{ $t('invoice.supplier_ventilation2_ht') }}</th>
								<th class="text-right">{{ $t('invoice.supplier_ventilation2_rate') }}</th>
								<th>{{ $t('invoice.supplier_ventilation2_charge_account') }}</th>
								<th class="text-right">{{ $t('invoice.supplier_ventilation2_vat') }}</th>
								<th>{{ $t('invoice.supplier_ventilation2_vat_account') }}</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="row in article_rows" :key="'article-' + row.details_id">
								<td>{{ row.label }}</td>
								<td class="text-right">{{ row.amount }}</td>
								<td class="text-right">{{ row.rate === null ? '' : rateLabel(row.rate) }}</td>
								<td class="invoice-supplier-ventilation__account">
									<e-select
										v-model="row.account"
										track-by="id"
										label="name"
										:options="charge_accounts"
										:searchable="true"
										:show-labels="false"
										:allow-empty="false"
										:placeholder="$t('compta.search_accounting_account')"
										@input="refresh"
									>
										<template slot="noOptions">{{ $t('global.list_empty') }}</template>
									</e-select>
								</td>
								<td class="text-right"><PriceInput v-model="row.vat_amount" @change="refresh" /></td>
								<td class="invoice-supplier-ventilation__account">
									<e-select
										v-model="row.vat_account"
										track-by="id"
										label="name"
										:options="vatAccountsFor(row.rate)"
										:disabled="!toCents(row.vat_amount)"
										:searchable="true"
										:show-labels="false"
										:allow-empty="false"
										:placeholder="$t('compta.search_accounting_account')"
										@input="refresh"
									>
										<template slot="noOptions">{{ $t('global.list_empty') }}</template>
									</e-select>
								</td>
							</tr>
						</tbody>
					</table>

					<button v-if="mode === 'rate'" class="btn btn-secondary mt-3" @click="addRateRow">
						<font-awesome-icon :icon="['fal', 'plus']" /> {{ $t('invoice.supplier_ventilation2_add') }}
					</button>

					<div class="mt-3">
						<ErrorAlert v-if="control && !control.amount" messageI18n="invoice.supplier_error_amount" />
						<ErrorAlert v-if="control && !control.vat" messageI18n="invoice.supplier_error_vat_amount" />

						<button class="btn btn-primary" :disabled="!can_save || saving" @click="onSave">
							{{ $t('global.enregistrer') }}
							<font-awesome-icon v-if="saving" :icon="['fal', 'spinner']" spin />
						</button>
					</div>

					<!-- Les mouvements comptables se lisent sous la ventilation qui les produit : c'est le seul
					     endroit où l'on voit, avant d'enregistrer, qu'une facture déjà livrée à l'expert-comptable
					     recevra une écriture de régularisation, et sur quels comptes. -->
					<div class="invoice-supplier-details__control mt-4">
						<h4>{{ $t('invoice.supplier_ventilation2_entries') }}</h4>

						<p v-if="!entries.length" class="invoice-supplier-details__hint">{{ $t('invoice.supplier_ventilation2_no_entry') }}</p>

						<FecLines v-for="entry in entries" :key="entry.key" :lines="entry.lines" class="mb-3">
							<template slot="title">
								{{ $t('invoice.supplier_ventilation2_entry_' + entry.kind, { date: entry.date }) }}
								<b-badge v-if="entry.exported_at" variant="success">{{ $t('invoice.supplier_ventilation2_delivered', { date: entry.exported_at }) }}</b-badge>
								<b-badge v-else variant="secondary">{{ $t('invoice.supplier_ventilation2_waiting') }}</b-badge>
								<!-- La référence du fichier reste lisible : c'est par elle que l'expert-comptable
								     retrouve l'écriture, mais ce n'est pas ce que l'utilisateur cherche ici. -->
								<div class="invoice-supplier-details__hint">
									{{ $t('invoice.supplier_ventilation2_entry_reference', { journal: entry.journal, num: entry.num, exercice: entry.exercice }) }}
								</div>
							</template>
						</FecLines>

						<h5 class="mt-4">{{ $t('invoice.supplier_ventilation2_planned') }}</h5>

						<p v-if="!preview" class="invoice-supplier-details__hint">{{ $t('invoice.supplier_ventilation2_planned_incomplete') }}</p>
						<ErrorAlert v-else-if="preview.mode === 'error'" :messageI18n="preview_error" />
						<template v-else>
							<p class="invoice-supplier-details__hint">{{ $t('invoice.supplier_ventilation2_planned_' + preview.mode, { date: preview.date }) }}</p>
							<FecLines v-if="preview.lines.length" :lines="preview.lines" :total="preview.total" />
						</template>
					</div>
				</template>
			</div>
		</div>

		<!-- Passer une OD sur une facture déjà livrée se décide en connaissance de cause : elle part dans
		     le prochain fichier de l'expert-comptable, et ne se reprendra que par une nouvelle écriture. -->
		<b-modal
			ref="confirmModal"
			size="lg"
			:title="$t('invoice.supplier_ventilation2_confirm_title')"
			:ok-title="$t('invoice.supplier_ventilation2_confirm_ok')"
			:cancel-title="$t('global.annuler')"
			@ok="save"
		>
			<p>{{ $t('invoice.supplier_ventilation2_confirm_od') }}</p>
			<FecLines v-if="preview" :lines="preview.lines" :total="preview.total" />
		</b-modal>
	</div>
</template>


<script type="text/javascript">
	import Accounting from "@/mixins/Accounting.js"
	import Invoice from "@/mixins/Invoice.js"
	import debounce from 'lodash/debounce'

	export default {
		name: "InvoiceSupplierFicheVentilation2",

		components: {
			ErrorAlert: () => import('GroomyRoot/components/Alert/ErrorAlert'),
			FecLines: () => import('@/components/InvoiceSupplier/Fiche/FecLines'),
			LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35'),
			PriceInput: () => import('GroomyRoot/components/Inputs/PriceInput')
		},

		mixins: [Accounting, Invoice],

		props: {
			invoice: { type: Object, default: () => ({}) },
			invoicesupplier_id: { type: [String, Number], default: null }
		},

		// mode : 'rate' pour une ligne par taux de TVA, 'article' pour une ligne par ligne de la facture.
		// Les taux sont partout des fractions (0.2), comme le vat_value des comptes de TVA.
		data() {
			return {
				loaded: false,
				saving: false,
				mode: 'rate',
				rate_rows: [],
				article_rows: [],
				charge_accounts: [],
				vat_accounts: [],
				most_used: {},
				has_horses: false,
				control: null,
				entries: [],
				preview: null,
				// Numéro du dernier contrôle lancé : une réponse arrivée après une saisie plus récente est
				// ignorée, elle rouvrirait l'enregistrement sur un état qui n'est plus celui de l'écran.
				refresh_id: 0,
				refreshing: null
			}
		},

		computed: {
			is_pdf() {
				return !!this.invoice.suppliersocr_pdf_url && this.invoice.suppliersocr_pdf_url.includes('.pdf')
			},

			/**
			 * La comptabilité arrondit les centimes au lieu de les tronquer. Le troncage est le réglage par
			 * défaut, et de très loin le plus répandu.
			 */
			rounds_centimes() {
				return this.invoice.accounting_plan && this.invoice.accounting_plan.accountingplan_format_centimes === 'arrondisCentimes'
			},

			/**
			 * La vue par article n'a de sens que si la facture porte ses lignes.
			 */
			mode_options() {
				return [
					{ value: 'rate', text: this.$t('invoice.supplier_ventilation2_mode_rate') },
					{ value: 'article', text: this.$t('invoice.supplier_ventilation2_mode_article'), disabled: !this.article_rows.length }
				]
			},

			/**
			 * Taux proposés : ceux des comptes de TVA de la comptabilité, un compte de TVA n'existant que
			 * pour un taux donné.
			 */
			rate_options() {
				const rates = [...new Set(this.vat_accounts.map(account => account.vat_value))].sort((a, b) => a - b)

				return [
					{ value: null, text: this.$t('invoice.supplier_ventilation2_rate_choose'), disabled: true },
					...rates.map(rate => ({ value: rate, text: this.rateLabel(rate) }))
				]
			},

			rows() {
				return this.mode === 'rate' ? this.rate_rows : this.article_rows
			},

			/**
			 * Une ligne est complète quand elle porte un compte de charge, et un compte de taxe dès
			 * qu'elle porte une taxe. C'est la règle du serveur, mot pour mot.
			 *
			 * Elle se lit sur le montant de taxe et non sur le taux : une facture dont le taux a été mal
			 * lu porte tout de même sa TVA, et il faut pouvoir lui donner son compte. Se fier au taux
			 * rendrait ces lignes impossibles à enregistrer — complètes à l'écran, refusées au serveur.
			 */
			complete() {
				return this.rows.length > 0 && this.rows.every(row => row.account && (!this.toCents(row.vat_amount) || row.vat_account))
			},

			can_save() {
				if(!this.preview || this.preview.mode === 'error') return false

				return this.complete && !!this.control && this.control.amount && this.control.vat
			},

			/**
			 * Motif d'un aperçu impossible. Le serveur le rend sous la forme « fec.code:précision » : seul
			 * le code se traduit.
			 */
			preview_error() {
				const code = this.preview.error.split(':')[0].replace('fec.', '')

				return 'invoice.supplier_ventilation2_fec_error.' + code
			}
		},

		watch: {
			// La fiche recharge la facture après chaque enregistrement : l'écran se refait alors sur la
			// ventilation et les écritures à jour.
			invoice: 'init_component'
		},

		methods: {
			/**
			 * Charge les comptes de la comptabilité, la ventilation existante et les lignes de la facture,
			 * puis ouvre l'écran sur la vue qui convient : celle qui a servi à ventiler la facture, ou pour
			 * une facture neuve la vue par article si ce fournisseur a déjà été ventilé ainsi.
			 */
			async init_component() {
				this.loaded = false

				if(!this.invoice.accounting_plan) {
					this.loaded = true
					return
				}

				const accounts = (await this.loadAccountingAccounts(this.invoice.accounting_plan.accountingplan_id)).filter(account => account.accountingaccount_supplier)
				this.charge_accounts = accounts.filter(account => account.accountingaccount_vat === null).map(account => this.accountOption(account))
				this.vat_accounts = accounts.filter(account => account.accountingaccount_vat !== null).map(account => ({
					...this.accountOption(account),
					vat: account.accountingaccount_vat,
					vat_value: parseFloat(account.vat.vat_value)
				}))

				this.most_used = await this.getChargeAccountSupplierMostUsed(this.invoice.suppliersocr_entity, this.invoice.suppliersocr_tiers)

				const details = await this.getInvoiceSupplierDetails(this.invoicesupplier_id)
				const charges = this.invoice.suppliersocr_export_status === 'to_prepare' ? [] : await this.getChargeAccountSupplier(this.invoicesupplier_id)

				this.has_horses = charges.some(charge => charge.ischargeaccount_horse)
				// Seule une ventilation déjà faite par article décrit ces lignes : reprendre une ventilation
				// par taux laisserait les comptes par défaut tout en sautant la reprise de l'arrondi.
				this.article_rows = this.articleRows(details, charges.filter(charge => charge.ischargeaccount_details))
				this.rate_rows = this.rateRows(details, charges.filter(charge => !charge.ischargeaccount_details))

				const by_article = charges.length ? charges.some(charge => charge.ischargeaccount_details) : this.most_used.details_mode
				this.mode = by_article && this.article_rows.length ? 'article' : 'rate'

				await this.loadEntries()
				this.loaded = true
				this.refresh()
			},

			accountOption(account) {
				return { id: account.accountingaccount_id, name: account.accountingaccount_number + " (" + account.accountingaccount_label + ")" }
			},

			chargeAccountOf(charge) {
				return this.charge_accounts.find(account => account.id == charge.ischargeaccount_accountingaccount) || null
			},

			vatAccountOf(charge) {
				return this.vat_accounts.find(account => account.id == charge.ischargeaccount_vataccount) || null
			},

			/**
			 * Lignes de la vue par taux.
			 *
			 * Une ventilation existante est reprise telle quelle. Sinon, par ordre de préférence : les
			 * taux lus sur le document, les lignes de la facture regroupées par taux, et à défaut le hors
			 * taxes et la TVA de la facture sur une seule ligne.
			 */
			rateRows(details, charges) {
				if(charges.length) {
					return charges.map(charge => {
						const vat_account = this.vatAccountOf(charge)

						return {
							amount: charge.ischargeaccount_amount.toString(),
							account: this.chargeAccountOf(charge),
							// Sans compte de TVA, la ligne n'est pas à taux inconnu : elle est à taux nul, et
							// c'est la taxe enregistrée qui le dit. La relire à null la rendrait indéfiniment
							// incomplète au rechargement, et l'aperçu des écritures ne serait jamais demandé.
							rate: vat_account ? vat_account.vat_value : (charge.ischargeaccount_vatamount == 0 ? 0 : null),
							vat_amount: charge.ischargeaccount_vatamount.toString(),
							vat_account
						}
					})
				}

				const taxes = this.invoiceTaxes()

				// La lecture optique donne le taux en pourcentage. Le taux se lit bien, même quand la base
				// et le montant manquent : une facture sans TVA rend couramment un bloc de taxe à zéro sans
				// base. Prise telle quelle, elle fabriquait une ventilation à zéro qui écrasait tout ce
				// que la facture annonçait par ailleurs.
				if(taxes.length && taxes.every(tax => tax.base != null)) {
					return taxes.map(tax => this.newRateRow(tax.rate / 100, this.toCents(tax.base), this.toCents(tax.value)))
				}

				// Un seul taux et pas de base : toute la facture y est soumise, et ses totaux la donnent.
				// Ils sont plus sûrs que la lecture optique, qui se contredit volontiers d'un champ à
				// l'autre, et ils sont déjà dans la devise de la comptabilité — là où la base lue, elle,
				// serait dans celle du document.
				if(taxes.length === 1) {
					const ht_cents = this.toCents(this.invoice.suppliersocr_ht)
					const rate = taxes[0].rate / 100

					return [this.newRateRow(rate, ht_cents, rate ? this.vatCentsFor(rate, ht_cents) : this.invoiceVatCents())]
				}

				if(details.length) {
					const groups = {}

					this.articleRows(details, []).forEach(row => {
						const key = row.rate === null ? 'null' : row.rate

						if(!groups[key]) groups[key] = { rate: row.rate, ht: 0, vat: 0 }

						groups[key].ht += row.ht_cents
						groups[key].vat += this.toCents(row.vat_amount)
					})

					return Object.values(groups).map(group => this.newRateRow(group.rate, group.ht, group.vat))
				}

				return [this.newRateRow(null, this.toCents(this.invoice.suppliersocr_ht), this.invoiceVatCents())]
			},

			newRateRow(rate, ht_cents, vat_cents) {
				return {
					amount: this.toEuros(ht_cents),
					account: this.defaultChargeAccount(),
					rate,
					vat_amount: this.toEuros(vat_cents),
					vat_account: this.defaultVatAccount(rate)
				}
			},

			/**
			 * Lignes de la vue par article, une par ligne de la facture.
			 *
			 * La ligne de facture ne porte pas sa TVA : elle se calcule sur son taux, et l'écart d'arrondi
			 * avec la TVA de la facture est repris sur la plus grosse ligne — sans quoi le contrôle des
			 * sommes, fait au centime, refuserait une ventilation pourtant juste.
			 */
			articleRows(details, charges) {
				const rows = details.map(line => {
					const charge = charges.find(item => item.ischargeaccount_details == line.isdetails_id)
					// Le détail porte son taux en pourcentage.
					const rate = line.isdetails_vat_percent === null ? null : parseFloat(line.isdetails_vat_percent) / 100
					const ht_cents = this.toCents(line.isdetails_ht)

					return {
						details_id: line.isdetails_id,
						label: line.isdetails_label,
						amount: this.toEuros(ht_cents),
						ht_cents,
						rate,
						account: charge ? this.chargeAccountOf(charge) : this.defaultChargeAccount(),
						vat_amount: charge ? charge.ischargeaccount_vatamount.toString() : this.toEuros(this.vatCentsFor(rate, ht_cents)),
						vat_account: charge ? this.vatAccountOf(charge) : this.defaultVatAccount(rate)
					}
				})

				if(!charges.length && rows.length) {
					const gap = this.invoiceVatCents() - rows.reduce((sum, row) => sum + this.toCents(row.vat_amount), 0)
					const target = rows.reduce((max, row) => Math.abs(this.toCents(row.vat_amount)) > Math.abs(this.toCents(max.vat_amount)) ? row : max, rows[0])

					target.vat_amount = this.toEuros(this.toCents(target.vat_amount) + gap)
				}

				return rows
			},

			defaultChargeAccount() {
				return this.charge_accounts.find(account => account.id == this.most_used.accounting_account) || null
			},

			/**
			 * Compte de TVA d'un taux : le seul de la comptabilité s'il n'y en a qu'un, sinon celui que ce
			 * fournisseur utilise le plus.
			 */
			defaultVatAccount(rate) {
				const candidates = this.vatAccountsFor(rate)

				if(candidates.length === 1) return candidates[0]
				if(!candidates.length) return null

				// Deux TVA peuvent porter le même taux : le compte le plus utilisé se cherche sur chacune.
				return candidates.find(account => account.id == this.most_used['vat_' + account.vat]) || null
			},

			/**
			 * Comptes de TVA d'un taux, tous les comptes tant que le taux n'est pas choisi.
			 */
			vatAccountsFor(rate) {
				if(rate === null) return this.vat_accounts

				return this.vat_accounts.filter(account => Math.abs(account.vat_value - rate) < 0.00001)
			},

			onRateChanged(row) {
				// Le compte suit le nouveau taux : une comptabilité qui tient un compte de TVA à zéro le
				// retrouve, les autres n'en ont aucun à proposer et la ligne s'en passe puisqu'elle ne
				// porte plus de taxe.
				row.vat_account = this.defaultVatAccount(row.rate)
				row.vat_amount = this.toEuros(this.vatCentsFor(row.rate, this.toCents(row.amount)))
				this.refresh()
			},

			addRateRow() {
				this.rate_rows.push(this.newRateRow(null, 0, 0))
			},

			removeRateRow(index) {
				this.rate_rows.splice(index, 1)
				this.refresh()
			},

			/**
			 * Ventilation telle que le serveur l'attend, montants en euros.
			 */
			payload() {
				return this.rows.map(row => ({
					ischargeaccount_accountingaccount: row.account.id,
					ischargeaccount_vataccount: row.vat_account ? row.vat_account.id : null,
					ischargeaccount_amount: this.toEuros(this.toCents(row.amount)),
					ischargeaccount_vatamount: this.toEuros(this.toCents(row.vat_amount)),
					ischargeaccount_details: row.details_id || null
				}))
			},

			/**
			 * Rejoue le contrôle des sommes et l'aperçu comptable sur l'état de l'écran. La promesse en
			 * cours est gardée : l'enregistrement l'attend avant de décider s'il doit demander confirmation.
			 */
			runRefresh() {
				this.refreshing = this.loadControlAndPreview(++this.refresh_id)

				return this.refreshing
			},

			async loadControlAndPreview(refresh_id) {
				const control = await this.checkInvoiceSupplierChargeSum(this.invoicesupplier_id, this.rows.map(row => ({
					amount: this.toCents(row.amount) / 100,
					vat: this.toCents(row.vat_amount) / 100
				})))

				if(refresh_id !== this.refresh_id) return
				this.control = control

				if(!this.complete) {
					this.preview = null
					return
				}

				const preview = await this.previewInvoiceSupplierFec(this.invoicesupplier_id, this.payload())

				if(refresh_id !== this.refresh_id || !preview) return

				// Une écriture comptable est en devise de tenue : la mettre en forme avec celle du
				// fournisseur afficherait un montant juste sous la mauvaise monnaie.
				const currency = this.invoice.suppliersocr_ledger_currency || 'EUR'

				this.preview = {
					...preview,
					date: preview.date ? this.formatDate(preview.date) : null,
					lines: await this.formatLines(preview.lines, currency),
					total: {
						debit: await this.priceFormat(preview.lines.reduce((sum, line) => sum + line.debit, 0) / 100, currency, true),
						credit: await this.priceFormat(preview.lines.reduce((sum, line) => sum + line.credit, 0) / 100, currency, true)
					}
				}
			},

			/**
			 * Écritures déjà passées au FEC. Le serveur les rend déjà groupées et nommées par leur
			 * nature ; seuls leurs montants et leurs dates restent à mettre en forme.
			 */
			async loadEntries() {
				const entries = await this.getInvoiceSupplierFec(this.invoicesupplier_id)
				// Une écriture comptable est en devise de tenue : la mettre en forme avec celle du
				// fournisseur afficherait un montant juste sous la mauvaise monnaie.
				const currency = this.invoice.suppliersocr_ledger_currency || 'EUR'

				for(const entry of entries) {
					entry.key = entry.journal + '|' + entry.exercice + '|' + entry.num
					entry.date = this.formatDate(entry.date)
					entry.exported_at = entry.exported_at ? this.formatDate(entry.exported_at) : null
					entry.lines = await this.formatLines(entry.lines, currency)
				}

				this.entries = entries
			},

			/**
			 * Lignes d'écriture prêtes à afficher : montants en centimes convertis dans la devise de la
			 * facture, un côté vide plutôt qu'un zéro.
			 */
			async formatLines(lines, currency) {
				const formatted = []

				for(const line of lines) {
					formatted.push({
						account: line.account,
						label: line.label,
						debit: line.debit ? await this.priceFormat(line.debit / 100, currency, true) : '',
						credit: line.credit ? await this.priceFormat(line.credit / 100, currency, true) : ''
					})
				}

				return formatted
			},

			/**
			 * Une régularisation sur une écriture déjà livrée à l'expert-comptable se confirme. La décision
			 * se prend sur l'aperçu de l'état courant : un aperçu encore différé ou en route est attendu,
			 * sans quoi un clic rapide après une saisie passerait l'OD sans l'avoir montrée.
			 */
			async onSave() {
				this.refresh.flush()
				await this.refreshing

				if(!this.can_save) return

				if(this.preview && ['od', 'od_replace'].includes(this.preview.mode) && this.preview.lines.length) {
					this.$refs.confirmModal.show()
					return
				}

				this.save()
			},

			async save() {
				this.saving = true

				// L'appel est rejeté sur un refus du serveur : sans reprise, le bouton resterait grisé et
				// l'écran muet. Le motif est le sien — paramétrage comptable, ou sommes qui ne retombent
				// pas sur la facture.
				try {
					await this.saveChargeAccountSupplier(this.invoicesupplier_id, this.payload())
				}
				catch(err) {
					this.failureToast('toast.info_save_failed')
					return
				}
				finally {
					this.saving = false
				}

				this.successToast('toast.info_save_succes')
				this.$emit('invoice-updated')
			},

			/**
			 * @param {Number} rate fraction, 0.2 pour 20 %
			 */
			rateLabel(rate) {
				return parseFloat((rate * 100).toFixed(2)) + ' %'
			},

			/**
			 * Les montants saisis sont des euros, les calculs se font en centimes entiers : sommer des
			 * euros flottants finit par perdre un centime.
			 */
			toCents(value) {
				const amount = String(value === null || value === undefined ? '' : value).toFloat()

				return isNaN(amount) ? 0 : Math.round(amount * 100)
			},

			toEuros(cents) {
				return (cents / 100).toFixed(2)
			},

			invoiceVatCents() {
				return this.toCents(this.invoice.suppliersocr_ttc) - this.toCents(this.invoice.suppliersocr_ht)
			},

			/**
			 * Ventilation de TVA lue sur le document, par taux.
			 *
			 * @return {Array}
			 */
			invoiceTaxes() {
				const ocr_data = this.invoice.suppliersocr_ocr_data ? JSON.parse(this.invoice.suppliersocr_ocr_data) : null

				return ocr_data && ocr_data.prediction && ocr_data.prediction.taxes ? ocr_data.prediction.taxes : []
			},

			/**
			 * TVA d'une ligne, en centimes.
			 *
			 * Le montant imprimé sur la facture est préféré au calcul dès qu'il porte sur la même base : le
			 * vendeur arrondit comme il l'entend, et le recalculer fabriquait un écart que le contrôle des
			 * sommes, fait au centime, refusait — sur une base de 5 338,68 € à 20 %, la facture annonce
			 * 1 067,73 € là où un arrondi au plus proche donne 1 067,74 €.
			 *
			 * À défaut, la règle est celle de la comptabilité : elle tronque les centimes par défaut, et ne
			 * les arrondit que si elle est configurée pour. C'est ce que fait déjà le circuit des ventes,
			 * via accountingplan_format_centimes.
			 *
			 * @param {Number} rate Taux en fraction (0.2), ou null pour une ligne sans taxe.
			 * @param {Number} ht_cents Base hors taxes de la ligne, en centimes.
			 * @return {Number}
			 */
			vatCentsFor(rate, ht_cents) {
				if(!rate) return 0

				const stated = this.invoiceTaxes().find(tax => tax.value !== null && tax.value !== undefined
					&& Math.abs(tax.rate / 100 - rate) < 0.00001
					&& this.toCents(tax.base === null || tax.base === undefined ? this.invoice.suppliersocr_ht : tax.base) === ht_cents)

				if(stated) return this.toCents(stated.value)

				// Le produit d'un entier par une fraction décimale tombe volontiers juste en dessous de
				// l'entier attendu : sans ce rattrapage, tronquer 100 000 centimes lus 99 999,999… en
				// retirerait un.
				const exact = ht_cents * rate
				const rounded = Math.round(exact)

				if(this.rounds_centimes || Math.abs(exact - rounded) < 0.000001) return rounded

				return Math.trunc(exact)
			}
		},

		// Différé par instance, pour ne pas interroger le serveur à chaque frappe : un debounce déclaré
		// dans methods serait partagé par toutes les instances du composant.
		created() {
			this.refresh = debounce(this.runRefresh, 300)
		},

		mounted() {
			const is_checked = this.invoice.suppliersocr_ocr_status === 'checked' || this.invoice.suppliersocr_acknowledged

			// L'onglet est masqué aux autres, mais le garde du routeur laisse passer tout utilisateur
			// des factures fournisseurs sur les onglets de la fiche : l'adresse reste atteignable.
			if(!is_checked || !this.$store.state.userAccess.hasAdminAccess) {
				this.$router.replace({ name: 'invoiceSupplierFicheOverview', params: { invoicesupplier_id: this.invoicesupplier_id } })
				return
			}

			this.init_component()
		},

		beforeDestroy() {
			this.refresh.cancel()
		}
	}
</script>
